import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import Tooltip from 'src/components/Tooltip/Tooltip'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { css } from 'styled-components'

interface TransactionAmountRangeFilterProps {
  minClearingBillingCentsFilter: number | undefined
  setMinClearingBillingCentsFilter: (value: number | undefined) => void
  maxClearingBillingCentsFilter: number | undefined
  setMaxClearingBillingCentsFilter: (value: number | undefined) => void
}

const centsToDollarsString = (cents: number | undefined) => {
  if (cents === undefined || cents === null) return ''
  return (cents / 100).toFixed(0)
}

const dollarsStringToCents = (input: string): number | undefined => {
  const trimmed = input.trim()
  if (trimmed === '') return undefined
  const n = Number(trimmed)
  if (!isFinite(n)) return undefined
  return Math.round(n * 100)
}

const TransactionAmountRangeFilter: React.FC<
  TransactionAmountRangeFilterProps
> = ({
  minClearingBillingCentsFilter,
  setMinClearingBillingCentsFilter,
  maxClearingBillingCentsFilter,
  setMaxClearingBillingCentsFilter,
}) => {
  const translate = useTranslate()

  const currencyFormatter = useCurrencyFormatter()

  // Local UI strings in dollars
  const [minDollars, setMinDollars] = useState<string>(
    centsToDollarsString(minClearingBillingCentsFilter),
  )
  const [maxDollars, setMaxDollars] = useState<string>(
    centsToDollarsString(maxClearingBillingCentsFilter),
  )

  // If parent cents change externally, sync inputs (but don’t fight the user while typing)
  useEffect(() => {
    setMinDollars(centsToDollarsString(minClearingBillingCentsFilter))
  }, [minClearingBillingCentsFilter])

  useEffect(() => {
    setMaxDollars(centsToDollarsString(maxClearingBillingCentsFilter))
  }, [maxClearingBillingCentsFilter])

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 12px;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 4px;
        `}
      >
        <div
          css={css`
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          {translate('Amount Range')}
        </div>
        <Tooltip
          openLeft={false}
          maxWidth={175}
          css={css`
            padding-right: 5px;
            z-index: 1;
          `}
        >
          {translate(
            'Transactions will be filtered by cleared amount based on your input',
          )}
        </Tooltip>
      </div>
      <div
        css={css`
          display: flex;
          gap: 6px;
          ${getFontCss(
            14,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {translate('Min')}
          <TextInputField
            design={TextInputFieldDesigns.New}
            placeholder={currencyFormatter({
              amount: 0,
              notation: 'compact',
            })}
            type={'number'}
            // When the value is undefined, clear the input to ''
            value={minDollars ?? ''}
            onChange={(val: string): void => {
              setMinClearingBillingCentsFilter(
                val ? dollarsStringToCents(val) : undefined,
              )
            }}
            disableDecimal
            disableExponent
            disablePositiveAndNegative
            disableWheel
          />
        </div>
        <div
          css={css`
            align-self: flex-end;
            bottom: 16px;
            position: relative;
          `}
        >
          {'-'}
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {translate('Max')}
          <TextInputField
            design={TextInputFieldDesigns.New}
            placeholder={currencyFormatter({
              amount: 0,
              notation: 'compact',
            })}
            type={'number'}
            // When the value is undefined, clear the input to ''
            value={maxDollars ?? ''}
            onChange={(val: string): void => {
              setMaxClearingBillingCentsFilter(
                val ? dollarsStringToCents(val) : undefined,
              )
            }}
            disableDecimal
            disableExponent
            disablePositiveAndNegative
            disableWheel
          />
        </div>
      </div>
    </div>
  )
}

export default TransactionAmountRangeFilter
